import { Add } from '@mui/icons-material'
import { useTheme, Chip, IconButton, Stack, TextField } from '@mui/material'
import { useFormikContext } from 'formik'
import { useState } from 'react'

import {
  ECampaignTemplateFormKeys,
  TCreateCampaignTemplateForm,
  TCreateTemplateFromCampaignForm,
} from '~/features/admin/models'
import rem from '~/utils/ui'

type TProps = {
  isLoading?: boolean
}

export default function CampaignTemplatesSearchKeywords({
  isLoading = false,
}: TProps) {
  const [searchKeywordValue, setSearchKeywordValue] = useState<string>('')
  const theme = useTheme()
  const formik = useFormikContext<
    TCreateCampaignTemplateForm | TCreateTemplateFromCampaignForm
  >()
  const { values, setFieldValue } = formik

  const handleSearchKeywordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchKeywordValue(event.target.value)
  }

  const handleAddSearchKeyword = () => {
    if (!searchKeywordValue.trim()) return

    const newSearchKeywords = [
      ...(values[ECampaignTemplateFormKeys.searchKeywords] || []),
      searchKeywordValue,
    ]
    setFieldValue(ECampaignTemplateFormKeys.searchKeywords, newSearchKeywords)
    setSearchKeywordValue('')
  }

  const handleSearchKeywordKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      handleAddSearchKeyword()
    }
  }

  const handleDeleteSearchKeyword = (searchKeyword: string) => {
    const newSearchKeywords =
      values[ECampaignTemplateFormKeys.searchKeywords]?.filter(
        (keyword) => keyword !== searchKeyword,
      ) || []
    setFieldValue(ECampaignTemplateFormKeys.searchKeywords, newSearchKeywords)
  }

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <TextField
          size="small"
          label="Search Keywords"
          value={searchKeywordValue}
          onChange={handleSearchKeywordChange}
          placeholder="Enter a search keyword"
          fullWidth
          onKeyDown={handleSearchKeywordKeyDown}
          disabled={isLoading}
        />
        <IconButton
          onClick={handleAddSearchKeyword}
          disabled={isLoading}
          sx={{
            borderRadius: rem(8),
            backgroundColor: theme.palette.secondary2,
            color: theme.palette.common.white,
            '&:hover': { color: theme.palette.text.primary },
          }}
        >
          <Add />
        </IconButton>
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap">
        {values[ECampaignTemplateFormKeys.searchKeywords]?.map(
          (searchKeyword) => (
            <Chip
              key={searchKeyword}
              label={searchKeyword}
              onDelete={() => handleDeleteSearchKeyword(searchKeyword)}
              disabled={isLoading}
              sx={{ borderRadius: rem(6) }}
              size="small"
            />
          ),
        )}
      </Stack>
    </Stack>
  )
}
